// src/pages/compony/BanksSection.jsx
import React, { useState, useEffect, useCallback } from 'react';
import api from '../../core/api';

const EMPTY = { iban: 'IR', bankName: '', holderName: '', accountNumber: '', cardNumber: '' };

const toEn = (s) =>
  String(s ?? '')
    .replace(/[۰-۹]/g, (d) => '۰۱۲۳۴۵۶۷۸۹'.indexOf(d))
    .replace(/[٠-٩]/g, (d) => '٠١٢٣٤٥٦٧٨٩'.indexOf(d));

const ltr = { style: { direction: 'ltr', textAlign: 'right' } };

const fmtCard = (c) => (c || '').replace(/(\d{4})(?=\d)/g, '$1 ');

export default function BanksSection({ onMessage }) {
  const [banks, setBanks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await api.get('/banks');
      setBanks(res.data?.banks || []);
    } catch (err) {
      onMessage?.(err.response?.data?.error || 'خطا در دریافت حساب‌های بانکی', 'danger');
    } finally {
      setLoading(false);
    }
  }, [onMessage]);

  useEffect(() => {
    load();
  }, [load]);

  const set = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  const reset = () => {
    setForm(EMPTY);
    setEditId(null);
  };

  const submit = async (e) => {
    e.preventDefault();

    const iban = toEn(form.iban).replace(/\s+/g, '').toUpperCase();
    if (!/^IR\d{24}$/.test(iban)) {
      onMessage?.('شماره شبا باید IR و ۲۴ رقم باشد', 'danger');
      return;
    }
    const card = toEn(form.cardNumber).replace(/[\s-]/g, '');
    if (card && !/^\d{16}$/.test(card)) {
      onMessage?.('شماره کارت باید ۱۶ رقم باشد', 'danger');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        iban,
        bankName: form.bankName.trim(),
        holderName: form.holderName.trim(),
        accountNumber: toEn(form.accountNumber).trim(),
        cardNumber: card,
      };
      if (editId) await api.post(`/banks/${editId}/edit`, payload);
      else await api.post('/banks', payload);

      onMessage?.(editId ? 'حساب بانکی ویرایش شد' : 'حساب بانکی اضافه شد', 'success');
      reset();
      load();
    } catch (err) {
      onMessage?.(err.response?.data?.error || 'خطا در ذخیره حساب بانکی', 'danger');
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (b) => {
    setEditId(b.id);
    setForm({
      iban: b.iban,
      bankName: b.bankName,
      holderName: b.holderName,
      accountNumber: b.accountNumber || '',
      cardNumber: b.cardNumber || '',
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const makeDefault = async (id) => {
    setBusyId(id);
    try {
      await api.post(`/banks/${id}/default`);
      onMessage?.('بانک پیش‌فرض تغییر کرد', 'success');
      await load();
    } catch (err) {
      onMessage?.(err.response?.data?.error || 'خطا در تغییر پیش‌فرض', 'danger');
    } finally {
      setBusyId(null);
    }
  };

  const remove = async (b) => {
    if (!window.confirm(`حساب «${b.bankName}» حذف شود؟`)) return;
    setBusyId(b.id);
    try {
      await api.post(`/banks/${b.id}/delete`);
      if (editId === b.id) reset();
      onMessage?.('حساب بانکی حذف شد', 'success');
      await load();
    } catch (err) {
      onMessage?.(err.response?.data?.error || 'خطا در حذف', 'danger');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="card">
      <h2>حساب‌های بانکی</h2>
      <p className="section-sub">
        حساب پیش‌فرض روی فاکتورها چاپ می‌شود. اولین حساب ثبت‌شده خودکار پیش‌فرض است.
      </p>

      {/* ============ فرم ============ */}
      <form onSubmit={submit} autoComplete="off">
        <div className="grid">
          <div className="field-full">
            <label>شماره شبا (IR...) *</label>
            <input
              required
              maxLength={26}
              placeholder="IR000000000000000000000000"
              value={form.iban}
              onChange={set('iban')}
              {...ltr}
            />
          </div>
          <div>
            <label>نام بانک *</label>
            <input required value={form.bankName} onChange={set('bankName')} />
          </div>
          <div>
            <label>نام صاحب حساب *</label>
            <input required value={form.holderName} onChange={set('holderName')} />
          </div>
          <div>
            <label>شماره حساب</label>
            <input value={form.accountNumber} onChange={set('accountNumber')} inputMode="numeric" {...ltr} />
          </div>
          <div>
            <label>شماره کارت</label>
            <input
              value={form.cardNumber}
              onChange={set('cardNumber')}
              inputMode="numeric"
              maxLength={19}
              placeholder="6037 0000 0000 0000"
              {...ltr}
            />
          </div>
        </div>

        <div className="flexend">
          {editId && (
            <button type="button" className="btn secondary" onClick={reset} disabled={saving}>
              <i className="fa-solid fa-xmark" /> انصراف
            </button>
          )}
          <button className="btn" type="submit" disabled={saving}>
            {!saving && <i className={`fa-solid ${editId ? 'fa-floppy-disk' : 'fa-plus'}`} />}
            <span className="btn-label">
              {saving ? 'در حال ذخیره...' : editId ? 'ذخیره تغییرات' : 'افزودن بانک'}
            </span>
          </button>
        </div>
      </form>

      {/* ============ لیست ============ */}
      <div style={{ marginTop: 22 }}>
        {loading ? (
          <div className="empty">در حال بارگذاری...</div>
        ) : banks.length === 0 ? (
          <div className="empty">هنوز حساب بانکی ثبت نشده است.</div>
        ) : (
          banks.map((b) => (
            <div className="list-item" key={b.id}>
              <div style={{ minWidth: 0 }}>
                <div>
                  <b>{b.bankName}</b> <span className="muted">— {b.holderName}</span>{' '}
                  {b.isDefault && (
                    <span className="badge">
                      <i className="fa-solid fa-star" /> پیش‌فرض
                    </span>
                  )}
                </div>
                <div className="muted" style={{ direction: 'ltr', textAlign: 'right', marginTop: 4 }}>
                  {b.iban}
                </div>
                <div className="muted" style={{ marginTop: 2 }}>
                  {b.accountNumber && <>حساب: {b.accountNumber.toLocaleString('fa-IR')}</>}
                  {b.accountNumber && b.cardNumber && ' — '}
                  {b.cardNumber && <>کارت: {fmtCard(b.cardNumber).replace(/\d/g, (d) => '۰۱۲۳۴۵۶۷۸۹'[d])}</>}
                </div>
              </div>

              <div className="row-actions">
                {!b.isDefault && (
                  <button
                    type="button"
                    className="btn secondary small"
                    onClick={() => makeDefault(b.id)}
                    disabled={busyId === b.id}
                  >
                    <i className="fa-regular fa-star" /> پیش‌فرض
                  </button>
                )}
                <button type="button" className="btn secondary small" onClick={() => startEdit(b)}>
                  <i className="fa-solid fa-pen" /> ویرایش
                </button>
                <button
                  type="button"
                  className="btn danger small"
                  onClick={() => remove(b)}
                  disabled={busyId === b.id}
                >
                  <i className="fa-solid fa-trash" /> حذف
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}